/// <amd-module name="Comments/Thread/Components/MarkdownCommentPreview" />
import { ICommentArtifactInfo } from "Comments/Common/Contracts";
import * as Resources from "Comments/Thread/Resources";
import * as React from "react";
import { ILegacyPlatformService } from "VSS/Legacy/Types";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface IMarkdownCommentPreviewProps {
    /**
     * Markdown text to preview
     */
    markdown: string;

    /**
     * Artifact info.
     */

    artifactInfo: ICommentArtifactInfo;

    /**
     * Handler for errors that come out of adding an item.
     */
    handleError(error: any): void;
}

interface IMarkdownCommentPreviewState {
    loading: boolean;
    renderedText: string;
}

const CommentsMarkDownRenderDataProviderContributionId = "ms.vss-comments.comments-markdown-render-data-provider";

/**
 * Component for showing the preview panel in the editor (internal)
 */
export class MarkdownCommentPreview extends VssComponent<IMarkdownCommentPreviewProps, IMarkdownCommentPreviewState> {
    private _mentionProcessModulePromise: Promise<any[]>;
    private _commentContentRef = React.createRef<HTMLDivElement>();

    constructor(props: IMarkdownCommentPreviewProps, context: IVssComponentContext) {
        super(props, context);

        this.state = { loading: true, renderedText: "" };

        const legacyPlatformService = context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
        this._mentionProcessModulePromise = legacyPlatformService.requireModules([
            "WorkItemTracking/Scripts/Utils/RichTextPreRenderUtility"
        ]);
    }

    public componentDidMount(): void {
        this._initialLoad();
    }

    public render(): JSX.Element {
        const { loading, renderedText } = this.state;
        const { markdown } = this.props;

        if (markdown && markdown.trim() && loading) {
            return (
                <span className="flex-column">
                    <Spinner size={SpinnerSize.medium} label={Resources.Loading} />
                </span>
            );
        }

        return (
            <div className="comment-preview">
                <div
                    className="comment-content rendered-mark-down"
                    ref={this._commentContentRef}
                    dangerouslySetInnerHTML={{ __html: renderedText }}
                />
            </div>
        );
    }

    private async _initialLoad(): Promise<void> {
        const { markdown, handleError } = this.props;

        if (markdown && markdown.trim()) {
            let renderedText: string | undefined;
            const contributionService: IVssContributionService = this.context.pageContext.getService<IVssContributionService>(
                "IVssContributionService"
            );
            const requestParameters = { ...this.props.artifactInfo, commentText: markdown };
            contributionService.getDataAsync<string>(CommentsMarkDownRenderDataProviderContributionId, requestParameters).then(
                (text: string | undefined) => {
                    renderedText = text;
                    this.setState({ loading: false, renderedText: renderedText || "" });
                    this._mentionProcessModulePromise.then(modules => {
                        modules[0].processHtmlWithMentions(renderedText, this._commentContentRef.current, {
                            onWorkItemClick: this._onWorkItemClick
                        });
                    });
                },
                (e: Error) => {
                    renderedText = `${Resources.Error}: ${e.message}`;
                    handleError(e);
                    this.setState({ loading: false, renderedText: renderedText || "" });
                }
            );
        }
    }

    private _onWorkItemClick = (workItem: any, url: string, defaultCallback: () => void): void => {
        onClickFPS(this.context.pageContext, url, true);
    };
}
